import jsPDF from 'jspdf';
import { TestResults } from '../../../types';
import { PDF_COLORS, getScoreColor } from '../config/colors';
import { PDF_FONTS, PDF_LAYOUT } from '../config/fonts';
import { PageManager } from '../helpers/pageBreaks';
import { testData } from '../../../data/testData';

export class VisualizationsComponent {
  private pdf: jsPDF;
  private pageManager: PageManager;

  constructor(pdf: jsPDF, pageManager: PageManager) {
    this.pdf = pdf;
    this.pageManager = pageManager;
  }

  addOverviewChart(results: TestResults): void {
    this.pageManager.checkForPageBreak(120);
    
    // Section title
    this.pdf.setFont(PDF_FONTS.primary, 'bold');
    this.pdf.setFontSize(PDF_FONTS.size.heading);
    this.pdf.setTextColor(...PDF_COLORS.primary);
    const titleY = this.pageManager.getCurrentY();
    this.pdf.text(
      'Export Readiness Overview',
      PDF_LAYOUT.margins.left,
      titleY
    );
    
    // Simple underline using rect instead of line
    this.pdf.setFillColor(...PDF_COLORS.primary);
    this.pdf.rect(
      PDF_LAYOUT.margins.left,
      titleY + 2,
      80,
      1,
      'F'
    );
    
    this.pageManager.setCurrentY(titleY + 15);
    
    // Chart container
    const chartX = PDF_LAYOUT.margins.left;
    const chartY = this.pageManager.getCurrentY();
    const chartWidth = this.pageManager.getContentWidth();
    const chartHeight = 100;
    
    // Container background
    this.pdf.setFillColor(...PDF_COLORS.background.white);
    this.pdf.setDrawColor(...PDF_COLORS.text.light);
    this.pdf.rect(chartX, chartY, chartWidth, chartHeight, 'FD');
    
    // Draw horizontal bar chart
    const barHeight = 8;
    const barSpacing = 12;
    const maxBarWidth = chartWidth - 100;
    const startY = chartY + 10;
    
    results.categories.forEach((category, index) => {
      const categoryInfo = testData.find(c => c.id === category.categoryId);
      if (!categoryInfo) return;
      
      const barY = startY + (index * barSpacing);
      const barWidth = (category.percentage / 100) * maxBarWidth;
      
      // Category name
      this.pdf.setFont(PDF_FONTS.primary, 'normal');
      this.pdf.setFontSize(PDF_FONTS.size.small);
      this.pdf.setTextColor(...PDF_COLORS.text.primary);
      
      const categoryName = categoryInfo.title.length > 20 
        ? categoryInfo.title.substring(0, 17) + '...'
        : categoryInfo.title;
      
      this.pdf.text(categoryName, chartX + 5, barY + 5);
      
      // Progress bar background
      this.pdf.setFillColor(...PDF_COLORS.background.light);
      this.pdf.rect(chartX + 80, barY, maxBarWidth, barHeight, 'F');
      
      // Progress bar
      const barColor = getScoreColor(category.percentage);
      this.pdf.setFillColor(...barColor);
      this.pdf.rect(chartX + 80, barY, barWidth, barHeight, 'F');
      
      // Percentage text
      this.pdf.setTextColor(...PDF_COLORS.text.primary);
      this.pdf.text(
        `${Math.round(category.percentage)}%`,
        chartX + 85 + maxBarWidth,
        barY + 5
      );
    });
    
    this.pageManager.setCurrentY(chartY + chartHeight + 20);
  }

  addScoreDistribution(results: TestResults): void {
    // Force new page for score distribution
    this.pdf.addPage();
    this.pageManager.resetToTop();
    
    // Section title
    this.pdf.setFont(PDF_FONTS.primary, 'bold');
    this.pdf.setFontSize(PDF_FONTS.size.subheading);
    this.pdf.setTextColor(...PDF_COLORS.primary);
    const titleY = this.pageManager.getCurrentY();
    this.pdf.text(
      'Score Distribution Analysis',
      PDF_LAYOUT.margins.left,
      titleY
    );
    
    // Simple underline
    this.pdf.setFillColor(...PDF_COLORS.primary);
    this.pdf.rect(
      PDF_LAYOUT.margins.left,
      titleY + 3,
      90,
      1,
      'F'
    );
    
    this.pageManager.setCurrentY(titleY + 20);
    
    // Calculate distribution
    const readyCount = results.categories.filter(c => c.status === 'Ready').length;
    const almostReadyCount = results.categories.filter(c => c.status === 'Almost Ready').length;
    const notReadyCount = results.categories.filter(c => c.status === 'Not Ready').length;
    const total = results.categories.length;
    
    // Create simple bar chart representation instead of pie chart
    const chartX = PDF_LAYOUT.margins.left + 20;
    const chartY = this.pageManager.getCurrentY();
    const chartWidth = this.pageManager.getContentWidth() - 40;
    
    // Draw distribution bars
    let currentX = chartX;
    const barHeight = 30;
    
    // Ready segment
    if (readyCount > 0) {
      const segmentWidth = (readyCount / total) * chartWidth;
      this.pdf.setFillColor(...PDF_COLORS.success);
      this.pdf.rect(currentX, chartY, segmentWidth, barHeight, 'F');
      currentX += segmentWidth;
    }
    
    // Almost Ready segment
    if (almostReadyCount > 0) {
      const segmentWidth = (almostReadyCount / total) * chartWidth;
      this.pdf.setFillColor(...PDF_COLORS.warning);
      this.pdf.rect(currentX, chartY, segmentWidth, barHeight, 'F');
      currentX += segmentWidth;
    }
    
    // Not Ready segment
    if (notReadyCount > 0) {
      const segmentWidth = (notReadyCount / total) * chartWidth;
      this.pdf.setFillColor(...PDF_COLORS.error);
      this.pdf.rect(currentX, chartY, segmentWidth, barHeight, 'F');
    }
    
    // Legend
    const legendX = chartX;
    let legendY = chartY + barHeight + 20;
    
    this.pdf.setFont(PDF_FONTS.primary, 'normal');
    this.pdf.setFontSize(PDF_FONTS.size.small);
    
    const addLegendItem = (color: [number, number, number], label: string, count: number) => {
      if (count === 0) return;
      
      const percentage = Math.round((count / total) * 100);
      
      this.pdf.setFillColor(...color);
      this.pdf.rect(legendX, legendY, 8, 6, 'F');
      this.pdf.setTextColor(...PDF_COLORS.text.primary);
      this.pdf.text(`${label} (${count} - ${percentage}%)`, legendX + 12, legendY + 4);
      legendY += 12;
    };
    
    addLegendItem(PDF_COLORS.success, 'Ready', readyCount);
    addLegendItem(PDF_COLORS.warning, 'Almost Ready', almostReadyCount);
    addLegendItem(PDF_COLORS.error, 'Not Ready', notReadyCount);
    
    this.pageManager.setCurrentY(legendY + 20);
  }

  addBenchmarkComparison(results: TestResults): void {
    this.pageManager.checkForPageBreak(80);
    
    // Section title
    this.pdf.setFont(PDF_FONTS.primary, 'bold');
    this.pdf.setFontSize(PDF_FONTS.size.subheading);
    this.pdf.setTextColor(...PDF_COLORS.primary);
    this.pdf.text(
      'Industry Benchmark Comparison',
      PDF_LAYOUT.margins.left,
      this.pageManager.getCurrentY()
    );
    
    this.pageManager.addParagraph();
    
    // Benchmark data
    const industryAverage = 65;
    const topPerformers = 85;
    
    const chartX = PDF_LAYOUT.margins.left;
    const chartY = this.pageManager.getCurrentY();
    const chartWidth = this.pageManager.getContentWidth();
    
    // Your score
    this.drawBenchmarkBar(
      'Your Score',
      results.overallPercentage,
      chartX,
      chartY,
      chartWidth,
      getScoreColor(results.overallPercentage)
    );
    
    // Industry average
    this.drawBenchmarkBar(
      'Industry Average',
      industryAverage,
      chartX,
      chartY + 20,
      chartWidth,
      PDF_COLORS.text.secondary
    );
    
    // Top performers
    this.drawBenchmarkBar(
      'Top Performers',
      topPerformers,
      chartX,
      chartY + 40,
      chartWidth,
      PDF_COLORS.success
    );
    
    this.pageManager.setCurrentY(chartY + 70);
  }

  private drawBenchmarkBar(
    label: string,
    percentage: number,
    x: number,
    y: number,
    maxWidth: number,
    color: [number, number, number]
  ): void {
    const barWidth = (percentage / 100) * (maxWidth - 100);
    
    // Label
    this.pdf.setFont(PDF_FONTS.primary, 'normal');
    this.pdf.setFontSize(PDF_FONTS.size.small);
    this.pdf.setTextColor(...PDF_COLORS.text.primary);
    this.pdf.text(label, x, y + 5);
    
    // Bar background
    this.pdf.setFillColor(...PDF_COLORS.background.light);
    this.pdf.rect(x + 80, y, maxWidth - 100, 8, 'F');
    
    // Bar
    this.pdf.setFillColor(...color);
    this.pdf.rect(x + 80, y, barWidth, 8, 'F');
    
    // Percentage
    this.pdf.text(`${Math.round(percentage)}%`, x + maxWidth - 15, y + 5);
  }
}